import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Amplify } from 'aws-amplify';
import { Hub } from 'aws-amplify/utils';
import { useEffect, useState, useCallback } from 'react';
import { isAuthenticated } from '@/api/auth';

// Layouts
import AppLayout from '@/components/layout/AppLayout';
import AuthLayout from '@/components/layout/AuthLayout';
import ErrorBoundary from '@/components/common/ErrorBoundary';
import ScrollToTop from '@/components/common/ScrollToTop';
import Spinner from '@/components/common/Spinner';

// Pages
import LandingPage from '@/pages/LandingPage';
import AboutPage from '@/pages/AboutPage';
import BlogPage from '@/pages/BlogPage';
import BlogPostMusicMarketingPlan from '@/pages/BlogPostMusicMarketingPlan';
import BlogPostShowFeelLikeSomething from '@/pages/BlogPostShowFeelLikeSomething';
import BlogPostFriendBaseToFanBase from '@/pages/BlogPostFriendBaseToFanBase';
import TutorialsPage from '@/pages/TutorialsPage';
import PrivacyPolicyPage from '@/pages/PrivacyPolicyPage';
import TermsOfServicePage from '@/pages/TermsOfServicePage';
import LoginPage from '@/pages/LoginPage';
import SignupPage from '@/pages/SignupPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import MessagesPage from '@/pages/MessagesPage';
import SetsPage from '@/pages/SetsPage';
import TasksPage from '@/pages/TasksPage';
import SettingsPage from '@/pages/SettingsPage';
import SubscriptionPage from '@/pages/SubscriptionPage';
import KitPage from '@/pages/KitPage';

// Configure AWS Amplify
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
      userPoolClientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
      loginWith: {
        oauth: {
          domain: import.meta.env.VITE_COGNITO_DOMAIN,
          scopes: ['openid', 'email', 'profile'],
          redirectSignIn: [import.meta.env.VITE_OAUTH_REDIRECT_SIGN_IN],
          redirectSignOut: [import.meta.env.VITE_OAUTH_REDIRECT_SIGN_OUT],
          responseType: 'code',
        },
      },
    },
  },
});

// Create TanStack Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 30 * 1000, // 30 seconds
    },
  },
});

/**
 * Route guard that redirects to login if the user is not authenticated.
 */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const [authChecked, setAuthChecked] = useState(false);
  const [isAuthed, setIsAuthed] = useState(false);

  useEffect(() => {
    isAuthenticated().then((authed) => {
      setIsAuthed(authed);
      setAuthChecked(true);
    });
  }, []);

  if (!authChecked) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isAuthed) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

/**
 * Route guard that redirects to app if the user is already authenticated.
 * Also listens for OAuth redirect completion (e.g., returning from Google sign-in).
 */
function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
  const [authChecked, setAuthChecked] = useState(false);
  const [isAuthed, setIsAuthed] = useState(false);

  const checkAuth = useCallback(() => {
    isAuthenticated().then((authed) => {
      setIsAuthed(authed);
      setAuthChecked(true);
    });
  }, []);

  useEffect(() => {
    checkAuth();

    // Listen for OAuth sign-in completion (user returning from Google redirect)
    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signInWithRedirect') {
        // OAuth sign-in completed successfully — re-check auth state
        checkAuth();
      }
      if (payload.event === 'signInWithRedirect_failure') {
        // OAuth sign-in failed — ensure we show the login page
        setAuthChecked(true);
        setIsAuthed(false);
      }
    });

    return unsubscribe;
  }, [checkAuth]);

  if (!authChecked) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (isAuthed) {
    return <Navigate to="/app/messages" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ScrollToTop />
        <ErrorBoundary>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/music-marketing-plan-for-indie-artists" element={<BlogPostMusicMarketingPlan />} />
            <Route path="/blog/your-show-has-to-feel-like-something" element={<BlogPostShowFeelLikeSomething />} />
            <Route path="/blog/how-to-turn-a-friend-base-into-a-fan-base" element={<BlogPostFriendBaseToFanBase />} />
            <Route path="/tutorials" element={<TutorialsPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms-of-service" element={<TermsOfServicePage />} />

            {/* Auth routes (redirect to app if already logged in) */}
            <Route
              element={
                <PublicOnlyRoute>
                  <AuthLayout />
                </PublicOnlyRoute>
              }
            >
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
            </Route>

            {/* Protected app routes */}
            <Route
              path="/app"
              element={
                <ProtectedRoute>
                  <AppLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Navigate to="/app/messages" replace />} />
              <Route path="messages" element={<MessagesPage />} />
              <Route path="messages/:id" element={<MessagesPage />} />
              <Route path="sets" element={<SetsPage />} />
              <Route path="sets/:id" element={<SetsPage />} />
              <Route path="tasks" element={<TasksPage />} />
              <Route path="tasks/history" element={<TasksPage />} />
              <Route path="tasks/:id" element={<TasksPage />} />
              <Route path="kit" element={<KitPage />} />
              <Route path="kit/link-in-bio" element={<KitPage />} />
              <Route path="kit/link-in-bio/edit" element={<KitPage />} />
              <Route path="kit/link-in-bio/analytics" element={<KitPage />} />
              <Route path="kit/microsite" element={<KitPage />} />
              <Route path="kit/microsite/edit" element={<KitPage />} />
              <Route path="kit/microsite/analytics" element={<KitPage />} />
              <Route path="kit/epk" element={<KitPage />} />
              <Route path="kit/epk/edit" element={<KitPage />} />
              <Route path="kit/epk/analytics" element={<KitPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="subscription" element={<SubscriptionPage />} />
            </Route>

            {/* Catch-all redirect */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </ErrorBoundary>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
